import { useMemo } from "react";

import { ProgressBarBreakpoint, ProgressBarState } from "../types";

import { ProgressBarContextProviderProps } from "./ProgressBarContextProvider";
import { useBreakpointBuilder } from "./useBreakpointBuilder";

export function useProgressBarState(
  props: ProgressBarContextProviderProps
): ProgressBarState {
  const builder = useBreakpointBuilder(props);
  const breakpoints = useMemo(() => builder.build(), [builder]);

  const value = props.cartValue ?? 0;

  return useMemo(() => {
    const clone = breakpoints.map((b) => {
      const breakpoint = { ...b };
      builder.expandIncomplete(breakpoint, value);
      return breakpoint;
    });

    let completedBreakpoint: ProgressBarBreakpoint | undefined = undefined;
    let nextBreakpoint: ProgressBarBreakpoint | undefined = clone[0];

    let progress = 0;
    for (let i = 0; i < clone.length; i++) {
      const breakpoint = clone[i];
      const breakpointStart = clone[i - 1]?.value ?? 0;
      const breakpointEnd = breakpoint.value;
      if (value < breakpointStart) {
        breakpoint.progress = 0;
      } else if (value < breakpointEnd) {
        breakpoint.progress =
          100 * ((value - breakpointStart) / (breakpointEnd - breakpointStart));
      } else {
        breakpoint.progress = 100;
        completedBreakpoint = breakpoint;
        nextBreakpoint = clone[i + 1];
      }
      progress += breakpoint.progress;
    }
    let remaining: number;
    const breakpointStart = breakpoints[breakpoints.length - 1]?.value ?? 0;
    const interval =
      breakpoints.length > 0
        ? Math.floor(breakpointStart / breakpoints.length)
        : 1;
    const breakpointEnd = breakpointStart + interval;
    if (value < breakpointStart) {
      remaining = 0;
    } else if (value < breakpointEnd) {
      remaining =
        100 * ((value - breakpointStart) / (breakpointEnd - breakpointStart));
    } else {
      remaining = 100;
    }
    return {
      progress: progress / clone.length,
      remaining,
      breakpoints: clone,
      completedBreakpoint,
      nextBreakpoint,
    };
  }, [breakpoints, value]);
}
